import React, { useEffect, useMemo, useState } from 'react';
import { formatCurrency } from '../utils/formatUtils';
import TermsBadge from './TermsBadge';
import { calculateRecipeCostDisplay } from '../utils/recipePricing';
import { API_BASE_URL } from '../config';
import { getRecipeMissingNames } from '../utils/recipeAvailability';
const PRICE_HINT_TEXT = 'Evde maaliyet ve dışarıdan maaliyet fiyatları varsayımdır. Sizlerin alışveriş sonrası fiyat girişleri ile sistem doğru sonuç vermeye başlayacaktır.';

const formatTlNoCents = (value) => {
  const n = Number(value || 0);
  return `${Number.isFinite(n) ? Math.round(n) : 0} ₺`;
};

const getIngredients = (recipe = {}) => {
  if (Array.isArray(recipe?.scaledIngredients) && recipe.scaledIngredients.length > 0) return recipe.scaledIngredients;
  if (Array.isArray(recipe?.malzemeler) && recipe.malzemeler.length > 0) return recipe.malzemeler;
  if (Array.isArray(recipe?.ingredients) && recipe.ingredients.length > 0) return recipe.ingredients;
  return [];
};

const getSteps = (recipe = {}) => {
  if (Array.isArray(recipe?.instructions) && recipe.instructions.length > 0) return recipe.instructions;
  if (Array.isArray(recipe?.steps) && recipe.steps.length > 0) return recipe.steps;
  return [];
};

const detectMealType = (recipe = {}) => {
  const tokens = [
    recipe?.serveStyle,
    recipe?.category,
    recipe?.mealType,
    recipe?.dishType,
    recipe?.recipeType,
    recipe?.name,
    recipe?.title
  ]
    .map((x) => String(x || '').toLocaleLowerCase('tr-TR'))
    .join(' ');
  if (/(corba|çorba|soup|sulu)/.test(tokens)) return 'soup';
  if (/(salata|salad|meze)/.test(tokens)) return 'salad';
  if (/(ana yemek|anayemek|main|yemek|et|tavuk)/.test(tokens)) return 'main';
  return 'other';
};

const resolveStepText = (step = {}, level = 'acemi') => {
  if (typeof step === 'string') return step;
  const ordered = level === 'sef'
    ? [step?.detay_sef, step?.detay_usta, step?.detay_acemi]
    : level === 'usta'
      ? [step?.detay_usta, step?.detay_acemi, step?.detay_sef]
      : [step?.detay_acemi, step?.detay_usta, step?.detay_sef];
  return ordered.find((x) => typeof x === 'string' && x.trim()) || step?.islem || step?.text || '';
};

const resolveBabyName = (userProfile = {}) => {
  const raw = userProfile?.babyName || userProfile?.bebek_ismi || userProfile?._babyNameFallback || '';
  const normalized = String(raw || '').trim();
  return normalized.length > 0 ? normalized : 'bebeğiniz';
};

const normalizeLandingStepText = (text = '', userProfile = {}) => {
  const babyName = resolveBabyName(userProfile);
  return String(text || '').replace(/\{?\s*bebek_ismi\s*\}?/gi, babyName);
};
const toLowerTr = (v = '') => String(v || '').toLocaleLowerCase('tr-TR');
const portionText = (recipe = {}, forcedPortion = null) => `${Number(forcedPortion || recipe?.servings || recipe?.portion || recipe?.kisi_sayisi || recipe?.serving_count || 4)} kişilik`;

const getMissingNames = (recipe = {}) => {
  return getRecipeMissingNames(recipe);
};

const isMissingByName = (name = '', missing = []) => {
  if (!name) return false;
  const n = toLowerTr(name);
  return missing.some((m) => {
    const x = toLowerTr(m);
    return x.includes(n) || n.includes(x);
  });
};

const lineText = (ing = {}, mode = 'traditional') => {
  if (typeof ing === 'string') return ing;
  if (mode === 'global') {
    return ing.displayTextGlobal || `${Math.round(Number(ing.quantity || ing.amount || ing.miktar || 0))} ${ing.unit || ing.birim || ''} ${ing.name || ''}`.trim();
  }
  return ing.displayTextTrTraditional || ing.turkmutfagi || `${ing.turkmutfagi_miktar || ing.amountRaw || ''} ${ing.turkmutfagi_birim || ing.unitRaw || ''} ${ing.name || ''}`.replace(/\s+/g, ' ').trim();
};

const parseMacro = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const m = String(value).replace(',', '.').match(/-?\d+(\.\d+)?/);
  return m ? Number(m[0]) : 0;
};

const getNutrition = (recipe = {}) => {
  const n = recipe?.nutrition || recipe?.nutritionalInfo || recipe?.nutritionalValues || recipe?.besinDegeri || {};
  return {
    protein: parseMacro(n?.protein ?? n?.protein_g ?? recipe?.protein ?? recipe?.proteinGrams),
    carbs: parseMacro(n?.carbs ?? n?.carb ?? n?.carbohydrate ?? recipe?.carbs ?? recipe?.carbGrams),
    fat: parseMacro(n?.fat ?? n?.fat_g ?? recipe?.fat ?? recipe?.fatGrams),
    calories: parseMacro(n?.calories ?? n?.kcal ?? recipe?.calories ?? recipe?.totalCalories)
  };
};

const getChefNote = (recipe = {}) => {
  const candidates = [
    recipe?.chefTip,
    recipe?.chef_note,
    recipe?.chefNote,
    recipe?.sef_notu,
    recipe?.sefinNotu,
    recipe?.presentation?.chefTip,
    recipe?.presentation?.chef_note,
    recipe?.presentation?.chefNote,
    recipe?.insight?.chefTip,
    recipe?.insight?.chef_note,
    recipe?.insight?.chefNote,
    recipe?.metadata?.chefTip,
    recipe?.metadata?.chef_note,
    recipe?.metadata?.chefNote
  ];
  const found = candidates.find((x) => typeof x === 'string' && x.trim());
  return found ? String(found).trim() : '';
};

export default function RecipeInsightModalMobile(props) {
  const { isOpen, onClose, recipe, isDarkMode, onAddToShoppingList, userPortion, onUsedSuccess, userProfile } = props;
  const [presentationMode, setPresentationMode] = useState('traditional');
  const [detailView, setDetailView] = useState('insight');
  const [skillLevel, setSkillLevel] = useState('acemi');
  const [showPriceHint, setShowPriceHint] = useState(false);
  const [markingAsUsed, setMarkingAsUsed] = useState(false);
  const ingredients = useMemo(() => getIngredients(recipe), [recipe]);
  const steps = useMemo(() => getSteps(recipe), [recipe]);
  const hasBabySignal = Boolean(
    userProfile?.hasBaby ||
    userProfile?.bebek_var_mi ||
    userProfile?.babyAgeGroup ||
    userProfile?.bebek_yas_grubu ||
    userProfile?.babyBirthDate ||
    userProfile?.babyName
  );
  const babySafetyText = normalizeLandingStepText(
    recipe?.babyAdjustment ||
    recipe?.babyInfo ||
    recipe?.babyNote ||
    'Tuz ve baharat eklemeden önce bebeğiniz için bir porsiyonu ayırıp kenara alın.',
    userProfile
  );
  const adaptiveSteps = useMemo(() => {
    const list = Array.isArray(steps) ? [...steps] : [];
    if (!hasBabySignal) return list;
    const hasBabyStep = list.some((step) => {
      const text = toLowerTr(resolveStepText(step, skillLevel));
      const type = toLowerTr(step?.type || '');
      return type === 'portion_split' || text.includes('bebek') || text.includes('porsiyon ayır') || text.includes('kenara ayır');
    });
    if (hasBabyStep) return list;
    list.unshift({
      type: 'portion_split',
      detay_acemi: babySafetyText,
      detay_usta: babySafetyText,
      detay_sef: babySafetyText
    });
    return list;
  }, [steps, hasBabySignal, babySafetyText, skillLevel]);
  const nutrition = useMemo(() => getNutrition(recipe), [recipe]);
  const missingNames = useMemo(() => getMissingNames(recipe), [recipe]);
  const chefNote = useMemo(() => getChefNote(recipe), [recipe]);
  const hasBrandRiskWarning = Boolean(recipe?._hasAllergenCollision);
  const hasGeneralWarning = !hasBrandRiskWarning && (
    (Array.isArray(recipe?.safetyFlags) && recipe.safetyFlags.length > 0) ||
    (Array.isArray(recipe?.safetyWarnings) && recipe.safetyWarnings.length > 0) ||
    recipe?.isBabySafe === false
  );
  useEffect(() => {
    if (isOpen) setDetailView('insight');
  }, [isOpen, recipe?.id]);

  if (!isOpen || !recipe) return null;

  const effectivePortion = Math.max(1, Number(userPortion || recipe?.targetPortion || recipe?.presentation?.appliedPortion || recipe?.servings || 1));
  const { displayedCostBreakdown, displayedCostTotal } = calculateRecipeCostDisplay(recipe, effectivePortion);
  const total = Number(displayedCostTotal || recipe?.priceSnapshot?.total || recipe?.totalCost || recipe?.presentation?.totalCost || 0);
  const weightedMatchRatio = Number(recipe?.insight?.match?.weightedMatchRatio || 0);
  // [Fix] %uyum — GÖSTERİLEN sayılardan (evde/toplam) türet (desktop ile aynı; ayrı
  // weightedMatchRatio bazı veri yollarında 0 gelip "X evde" ile çelişiyordu).
  const _homeCount = ingredients.filter((ing) => !isMissingByName(typeof ing === 'string' ? ing : (ing?.name || ''), missingNames)).length;
  const matchPercent = ingredients.length > 0
    ? Math.round((_homeCount / ingredients.length) * 100)
    : Math.max(0, Math.min(100, Math.round(weightedMatchRatio * 100)));
  const isFullReady = matchPercent >= 100 && missingNames.length === 0;
  const mealType = detectMealType(recipe);
  const outsideMultiplierByType = { soup: 2.2, main: 2.9, salad: 2.0, other: 2.5 };
  const inferredOutsideFallback = total * (outsideMultiplierByType[mealType] || outsideMultiplierByType.other);
  const outsidePrice = Number(recipe?.priceSnapshot?.outsidePrice ?? recipe?.presentation?.outsidePrice ?? inferredOutsideFallback) || 0;
  const saving = Math.max(0, outsidePrice - total);
  const rescuedCount = Number(recipe?.insight?.skt?.rescuedCount || 0);
  const available = ingredients.filter((ing) => !isMissingByName(typeof ing === 'string' ? ing : (ing?.name || ''), missingNames));
  const handleAddMissing = () => typeof onAddToShoppingList === 'function' && onAddToShoppingList(missingNames);
  const handleMarkAsUsed = async () => {
    if (markingAsUsed) return;
    setMarkingAsUsed(true);
    try {
      const token = localStorage.getItem('authToken');
      const usedPortion = Number(userPortion || recipe?.targetPortion || recipe?.presentation?.appliedPortion || recipe?.servings || 1);
      const response = await fetch(`${API_BASE_URL}/api/recipes/used`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({
          recipeData: recipe,
          rating: null,
          usedPortion,
          cookingNotes: `Cooked for ${usedPortion} portions.`
        })
      });
      if (!response.ok) throw new Error('recipe_used_failed');
      if (typeof onUsedSuccess === 'function') onUsedSuccess(recipe);
      onClose();
    } catch (_) {
      // Silent failure fallback: keep modal open for retry.
    } finally {
      setMarkingAsUsed(false);
    }
  };

  const modeCard = isDarkMode ? 'border border-[#2a3348] bg-[#171f33] text-[#dae2fd]' : 'border border-slate-300 bg-white text-slate-900';
  const modalBg = isDarkMode ? 'bg-[#0b1326] text-[#dae2fd] border-[#2a3348]' : 'bg-[#f3f6fb] text-slate-900 border-slate-300';
  const mutedText = isDarkMode ? 'text-[#bccbb9]' : 'text-slate-600';

  return (
    <div className="fixed inset-0 z-[260] bg-[#060e20]/40 backdrop-blur-md p-0" onClick={onClose}>
      <div className={`relative h-[100dvh] w-full overflow-hidden border ${modalBg}`} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className={`absolute right-3 top-3 z-30 rounded-full p-2 text-lg leading-none ${isDarkMode ? 'text-[#dae2fd]/85 hover:bg-white/10' : 'text-slate-700 hover:bg-slate-200'}`}>✕</button>

        <div className="h-full overflow-y-auto px-3 pb-[calc(7.5rem+env(safe-area-inset-bottom))] pt-3 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          <div className="mb-3 rounded-full bg-red-500/15 px-3 py-1 text-[10px] font-bold text-red-500 w-fit">SANA ÖZEL ÖNERİ</div>
          <h2 className="pr-10 text-3xl font-black leading-tight">{recipe?.name || recipe?.title || 'Tarif'}</h2>
          <div className="mt-2 flex items-center gap-1.5">
            <TermsBadge className="!static !top-auto !right-auto" />
            {hasBrandRiskWarning && (
              <div
                title="Mutfaktaki urunle cakisan alerjen/risk uyarisi var"
                className={`inline-flex size-7 items-center justify-center rounded-full border text-[12px] font-black ${isDarkMode ? 'border-red-500/40 bg-red-500/20 text-red-300' : 'border-red-200 bg-red-50 text-red-600'}`}
              >
                !
              </div>
            )}
            {hasGeneralWarning && (
              <div
                title="Tarif icin genel kullanim/guvenlik uyarisi var"
                className={`inline-flex size-7 items-center justify-center rounded-full border text-[12px] font-black ${isDarkMode ? 'border-amber-500/40 bg-amber-500/20 text-amber-300' : 'border-amber-200 bg-amber-50 text-amber-700'}`}
              >
                !
              </div>
            )}
          </div>
          <p className={`mt-1 text-xs ${mutedText}`}>Dolabındaki malzemelerle, tam zamanı!</p>

          <div className="mt-3 overflow-hidden rounded-2xl border border-white/10">
            <img src={String(recipe?.imageUrl || recipe?.image_url || '/images/default-food.jpg').trim().startsWith('/uploads/') || String(recipe?.imageUrl || recipe?.image_url || '/images/default-food.jpg').trim().startsWith('/api/uploads/') ? `${API_BASE_URL}${String(recipe?.imageUrl || recipe?.image_url || '/images/default-food.jpg').trim()}` : String(recipe?.imageUrl || recipe?.image_url || '/images/default-food.jpg').trim().startsWith('uploads/') ? `${API_BASE_URL}/${String(recipe?.imageUrl || recipe?.image_url || '/images/default-food.jpg').trim()}` : (recipe?.imageUrl || recipe?.image_url || '/images/default-food.jpg')} alt={recipe?.name || 'Tarif görseli'} className="aspect-[4/3] w-full object-cover" />
          </div>

          <div className={`mt-3 rounded-2xl p-3 ${modeCard}`}>
            <div className={`text-[10px] font-black uppercase tracking-widest ${mutedText}`}>Bu tarif seni bugün</div>
            <div className="mt-1 text-4xl font-black leading-none text-red-500">{formatTlNoCents(saving)}</div>
            <div className="mt-1 flex items-center gap-2 text-lg font-black uppercase">
              <span>kazandırıyor!</span>
              <div className="relative">
                <button
                  type="button"
                  title={PRICE_HINT_TEXT}
                  onClick={() => setShowPriceHint((prev) => !prev)}
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-red-400/60 bg-red-500/20 text-[11px] font-black text-red-300"
                >
                  !
                </button>
                {showPriceHint && (
                  <div className={`absolute bottom-full left-1/2 mb-2 z-30 w-56 max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-xl border p-2 text-[11px] normal-case leading-relaxed shadow-xl ${isDarkMode ? 'border-slate-600 bg-slate-900 text-slate-200' : 'border-slate-200 bg-white text-slate-700'}`}>
                    {PRICE_HINT_TEXT}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
              <div><div className={mutedText}>Dışarıda yesen</div><div className="font-black text-rose-400">≈ {formatTlNoCents(outsidePrice)}</div></div>
              <div><div className={mutedText}>Evde maliyetin</div><div className="font-black text-red-500">{formatTlNoCents(total)}</div></div>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-3 gap-2">
            <div className={`${modeCard} rounded-xl p-2 text-center`}><span className="material-symbols-outlined text-red-500 text-base">schedule</span><div className="text-[9px] uppercase">Hazırlık</div><div className="font-black">{recipe?.cookTime || recipe?.cookingTime || 25} dk</div></div>
            <div className={`${modeCard} rounded-xl p-2 text-center`}><span className="material-symbols-outlined text-red-500 text-base">groups</span><div className="text-[9px] uppercase">Porsiyon</div><div className="font-black">{portionText(recipe, effectivePortion)}</div></div>
            <div className={`${modeCard} rounded-xl p-2 text-center`}><span className="material-symbols-outlined text-red-500 text-base">bar_chart</span><div className="text-[9px] uppercase">Zorluk</div><div className="font-black">{recipe?.difficulty || 'Orta'}</div></div>
          </div>

          {detailView === 'insight' ? (
            <>
              <div className={`mt-3 rounded-2xl p-3 ${modeCard}`}>
                <div className="mb-2 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest"><span className={`material-symbols-outlined text-sm ${isFullReady ? 'text-emerald-500' : 'text-red-500'}`}>inventory_2</span>Malzeme Uyumu</div>
                <div className="text-sm">Evde: <b>%{matchPercent}</b> uyum</div>
                <div className="mt-1 text-xs">{available.length} malzeme evde, {missingNames.length} malzeme eksik</div>
                <button
                  onClick={handleAddMissing}
                  disabled={isFullReady}
                  className={`mt-3 w-full rounded-xl border py-2 text-[11px] font-black uppercase tracking-widest ${isFullReady
                    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-500'
                    : 'border-red-500/40 bg-red-500/10 text-red-500'
                    }`}
                >
                  {isFullReady ? 'Tüm malzemeler hazır' : 'Eksikleri Listeye Ekle'}
                </button>
              </div>

              <div className={`mt-3 rounded-2xl p-3 ${modeCard}`}>
                <div className="mb-2 text-[11px] font-black uppercase tracking-widest">Maliyet Analizi</div>
                <div className="space-y-1 text-sm">
                  {ingredients.slice(0, 8).map((ing, idx) => {
                    const n = typeof ing === 'string' ? ing : (ing?.name || `Malzeme ${idx + 1}`);
                    const scaledLine = Number(displayedCostBreakdown?.[idx] || 0);
                    const row = (recipe?.priceSnapshot?.breakdown || []).find((x) => toLowerTr(x?.name || '') === toLowerTr(n));
                    const c = scaledLine > 0 ? scaledLine : Number(row?.total || 0);
                    const miss = isMissingByName(n, missingNames);
                    return <div key={`${n}-${idx}`} className="flex items-center justify-between border-b border-white/10 py-1"><span className={miss ? 'text-rose-400 font-semibold' : ''}>{n}</span><span className={`font-bold ${miss ? 'text-rose-300' : ''}`}>{c > 0 ? formatCurrency(c) : '-'}{miss ? ' • Eksik' : ''}</span></div>;
                  })}
                </div>
                <div className="mt-2 flex items-center justify-between text-base font-black"><span>Toplam</span><span className="text-emerald-400">{formatTlNoCents(total)}</span></div>
              </div>

              <div className={`mt-3 rounded-2xl p-3 ${modeCard}`}>
                <div className="mb-2 text-[11px] font-black uppercase tracking-widest">Besin Değeri (Porsiyon)</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="rounded-xl bg-white/5 p-2"><div className={`text-[10px] ${mutedText}`}>Protein</div><div className="text-lg font-black">{Number(nutrition?.protein || 0)} g</div></div>
                  <div className="rounded-xl bg-white/5 p-2"><div className={`text-[10px] ${mutedText}`}>Karbonhidrat</div><div className="text-lg font-black">{Number(nutrition?.carbs || 0)} g</div></div>
                  <div className="rounded-xl bg-white/5 p-2"><div className={`text-[10px] ${mutedText}`}>Yağ</div><div className="text-lg font-black">{Number(nutrition?.fat || 0)} g</div></div>
                  <div className="rounded-xl bg-white/5 p-2"><div className={`text-[10px] ${mutedText}`}>Kalori</div><div className="text-lg font-black">{Number(nutrition?.calories || 0)} kcal</div></div>
                </div>
              </div>

              <div className={`mt-3 rounded-2xl p-3 ${modeCard}`}>
                <div className="mb-2 text-[11px] font-black uppercase tracking-widest">Neden Bu Tarif?</div>
                <div className="space-y-1 text-sm">
                  <div>• Evdeki malzemelerin <b>%{matchPercent}</b> kullanılıyor.</div>
                  <div>• <b>{missingNames.length}</b> malzeme eksik.</div>
                  <div>• Dışarıya göre <b>{formatTlNoCents(saving)}</b> tasarruf.</div>
                  <div>• SKT etkisi: <b>{rescuedCount}</b> ürün.</div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={`mt-3 rounded-2xl p-3 ${modeCard}`}>
                <div className="mb-3 flex items-center justify-between">
                  <div className="text-[11px] font-black uppercase tracking-widest">Malzemeler</div>
                  <div className="flex gap-1 text-[11px]">
                    <button onClick={() => setPresentationMode('traditional')} className={`rounded-full px-2 py-1 font-bold ${presentationMode === 'traditional' ? 'bg-red-500 text-white' : 'bg-white/10'}`}>Geleneksel</button>
                    <button onClick={() => setPresentationMode('global')} className={`rounded-full px-2 py-1 font-bold ${presentationMode === 'global' ? 'bg-indigo-500 text-white' : 'bg-white/10'}`}>Dünya</button>
                  </div>
                </div>
                <div className="space-y-2">
                  {ingredients.map((ing, idx) => {
                    const n = typeof ing === 'string' ? ing : (ing?.name || '');
                    const miss = isMissingByName(n, missingNames);
                    const missingClass = isDarkMode
                      ? 'bg-rose-500/25 border border-rose-300/30 text-white'
                      : 'bg-rose-200 border border-rose-400/60 text-slate-900';
                    return <div key={`${n}-${idx}`} className={`rounded-xl px-3 py-2 text-sm ${miss ? missingClass : 'bg-white/5'}`}>{lineText(ing, presentationMode)}</div>;
                  })}
                </div>
              </div>

              <div className={`mt-3 rounded-2xl p-3 ${modeCard}`}>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div className="text-[11px] font-black uppercase tracking-widest">Tarif Yapilisi</div>
                  <div className="flex items-center gap-1">
                    {[
                      { key: 'acemi', label: 'Acemi' },
                      { key: 'usta', label: 'Usta' },
                      { key: 'sef', label: 'Sef' }
                    ].map((item) => (
                      <button
                        key={item.key}
                        onClick={() => setSkillLevel(item.key)}
                        className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase ${skillLevel === item.key ? 'bg-red-500 text-white' : 'bg-white/10'}`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  {adaptiveSteps.length === 0 && <div className="rounded-xl bg-white/5 p-3 text-sm">Tarif adımı bulunamadı.</div>}
                  {hasBabySignal && (
                    <div className={`rounded-xl border p-3 text-sm ${isDarkMode ? 'border-[#F59E0B] bg-[#F59E0B]/25 text-[#FFF4DB]' : 'border-[#F59E0B] bg-[#F59E0B]/30 text-black'}`}>
                      Bebek Notu: {babySafetyText}
                    </div>
                  )}
                  {adaptiveSteps.map((step, idx) => (
                    <div key={idx} className="rounded-xl bg-white/5 p-3 text-sm">
                      <div className={`mb-1 flex items-center gap-2 text-[10px] font-black uppercase ${step?.type === 'portion_split' ? (isDarkMode ? 'text-[#FFF4DB]' : 'text-black') : 'text-red-400'}`}>
                        <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[9px] ${step?.type === 'portion_split' ? 'bg-[#F59E0B] text-black' : 'bg-red-500 text-white'}`}>
                          {idx + 1}
                        </span>
                        {step?.type === 'portion_split' ? `👶 Adım ${idx + 1}` : `Adım ${idx + 1}`}
                      </div>
                      <div>{normalizeLandingStepText(resolveStepText(step, skillLevel), userProfile)}</div>
                    </div>
                  ))}
                  {chefNote && (
                    <div className={`rounded-xl p-3 text-sm border ${isDarkMode ? 'border-amber-300/35 bg-amber-500/12' : 'border-amber-400/70 bg-amber-100'}`}>
                      <div className={`mb-1 text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-amber-300' : 'text-amber-800'}`}>Şefin Notu</div>
                      <div className={`${isDarkMode ? 'text-amber-100' : 'text-slate-900 font-medium'}`}>{chefNote}</div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30 border-t border-white/10 bg-[#0b1326]/95 px-3 py-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] backdrop-blur">
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (detailView === 'recipe') setDetailView('insight');
              }}
              className={`pointer-events-auto rounded-xl px-3 py-2 text-xs font-black ${isDarkMode ? 'border border-[#2a3348] bg-[#171f33] text-[#dae2fd]' : 'border border-slate-400 bg-white text-slate-900'}`}
            >
              {detailView === 'insight' ? 'Paylas' : '⬅️'}
            </button>
            <button
              onClick={() => {
                if (detailView === 'insight') {
                  setDetailView('recipe');
                  return;
                }
                handleMarkAsUsed();
              }}
              className={`pointer-events-auto flex-1 rounded-xl px-3 py-2 text-xs font-black text-white ${detailView === 'insight' && isFullReady
                ? 'bg-emerald-500 animate-pulse'
                : 'bg-[#ef4444]'
                }`}
            >
              {detailView === 'insight' ? 'HEMEN TARIFI GOR' : (markingAsUsed ? 'ISLENIYOR...' : 'KULLANDIM')}
            </button>
            <button className={`pointer-events-auto rounded-xl px-3 py-2 text-xs font-black ${isDarkMode ? 'border border-[#2a3348] bg-[#171f33] text-[#dae2fd]' : 'border border-slate-400 bg-white text-slate-900'}`}>❤️</button>
          </div>
        </div>
      </div>
    </div>
  );
}

